import type { AxiosError } from "axios";
import { hasStructuredResourceAccessDeniedPayload } from "@/utils/errorUtils";

const AGENT_REVIEW_BASE = "/agentic-v2/agent-review";
const FA_CLIENT_OID = /\/agentic-v2\/fa-clients\/[a-f0-9]{24}$/i;
const FA_CLIENT_DG_OID =
  /\/agentic-v2\/fa-clients\/[a-f0-9]{24}\/data-gathering$/i;
const AGENTIC_TASK_READ =
  /\/agentic-v2\/tasks\/[^/?#]+(?:\/messages)?$/i;

/**
 * Returns whether a failed axios response should be excluded from automatic Sentry reporting.
 *
 * Some endpoints treat 404 or resource-access 403 as expected, user-facing outcomes (stale
 * deep links, optional resources, portfolio/product permission checks). The UI handles those
 * cases; reporting them as production errors creates noise without actionable signal.
 *
 * @param error - Axios error from the shared API client interceptor
 * @returns `true` when the error should not be sent to Sentry
 */
export function shouldSkipSentryForApiError(error: AxiosError): boolean {
  const status = error.response?.status;
  const url = error.config?.url ?? "";
  const method = (error.config?.method ?? "get").toUpperCase();

  // Resource permission checks — portfolio/product pages show Access Denied UI.
  if (
    status === 403 &&
    (url.includes("/data/snapshot") ||
      hasStructuredResourceAccessDeniedPayload(error))
  ) {
    return true;
  }

  if (status !== 404) {
    return false;
  }

  // Dashboard widgets may keep a deleted screener view id; UI falls back to saved columns.
  if (method === "GET" && /\/screener\/views\/[^/?#]+$/.test(url)) {
    return true;
  }

  // Draft-save 404s are expected when the API predates the /progress route.
  if (url.includes("/admin/ops-checklists/") && url.includes("/progress")) {
    return true;
  }

  if (method === "GET") {
    // Stale client deep links, wrong-tenant bookmarks, or post-company-switch windows.
    if (FA_CLIENT_OID.test(url) || FA_CLIENT_DG_OID.test(url)) {
      return true;
    }
    // Task thread opened from a client/mission URL after the task was cleared.
    if (AGENTIC_TASK_READ.test(url)) {
      return true;
    }
  }

  if (!url.includes(AGENT_REVIEW_BASE)) {
    return false;
  }

  // Opening a case by run id — run may have been cleared or the bookmark is stale.
  if (method === "GET" && /\/agentic-v2\/agent-review\/runs\/[^/?#]+$/.test(url)) {
    return true;
  }

  // Optional review/comparison fetches on resume or Ace trial panels.
  if (method === "GET" && url.includes(`${AGENT_REVIEW_BASE}/reviews/`)) {
    return true;
  }

  // Import search with no matching task or interaction.
  if (method === "GET" && url.includes(`${AGENT_REVIEW_BASE}/import/lookup`)) {
    return true;
  }

  return false;
}
